import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const laCroixValmerEn: ChauffeurPage = {
  slug: "la-croix-valmer",
  title: "VTC & private chauffeur in La Croix-Valmer",
  subtitle:
    "Private chauffeur in La Croix-Valmer: the Gigaro beaches, vineyards and Cap Lardier, Nice airport transfers and hourly hire.",
  keywords: [
    "vtc la croix-valmer",
    "private chauffeur la croix-valmer",
    "la croix-valmer airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in La Croix-Valmer",
    subtitle:
      "The Gigaro beaches, the vineyards and Cap Lardier: Nice airport transfer, hourly hire and long distance, 7 days a week.",
    keywords: [
      "VTC La Croix-Valmer",
      "Gigaro beaches chauffeur",
      "Nice airport transfer",
      "Gulf of Saint-Tropez chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "VTC & private chauffeur in La Croix-Valmer (Gulf of Saint-Tropez)",
    badges: [
      "Gigaro beaches & Cap Lardier",
      "Nice airport transfer",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in La Croix-Valmer",
    items: [
      {
        title: "Gigaro beaches",
        description:
          "Access to the Gigaro beaches and the start of the coastal path: drop-off and pick-up with no parking worry in summer.",
      },
      {
        title: "Preserved nature & vineyards",
        description:
          "Cap Lardier, coves and wine estates: one chauffeur to roam with no constraint.",
      },
      {
        title: "Long-distance transfer",
        description:
          "From Nice airport, around 1h20 to 1h30 of road handled in high-end comfort.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in La Croix-Valmer",
    services: [
      {
        title: "Nice airport transfer",
        text: "La Croix-Valmer ⇄ Nice Côte d’Azur airport with flight tracking; a longer run than from Cannes, timed at departure.",
      },
      {
        title: "Saint-Raphaël TGV station",
        text: "Pick-up at Saint-Raphaël station, around 30 to 40 minutes away, as a rail alternative.",
      },
      {
        title: "Gigaro beach shuttles",
        text: "To the Gigaro beaches and Cap Lardier, round trip, with no parking constraint.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day for the beaches, the vineyards and Saint-Tropez.",
      },
      {
        title: "Long distance",
        text: "Trips of any distance from La Croix-Valmer: Cannes, Nice, Monaco and beyond, with a dedicated chauffeur.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Gigaro beaches",
        description:
          "Beaches and coves facing the islands, the commune’s seaside heart.",
      },
      {
        name: "Cap Lardier",
        description: "Protected natural area and the coastal path.",
      },
      {
        name: "Wine estates",
        description: "Côtes de Provence vineyards around the village.",
      },
      {
        name: "La Croix-Valmer village",
        description: "Village centre and viewpoints over the gulf.",
      },
      {
        name: "Saint-Raphaël station",
        description: "TGV station around 30-40 min away, a transfer point.",
      },
      {
        name: "Nice airport",
        description: "Nice Côte d’Azur airport, around 1h20-1h30 away.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur for La Croix-Valmer",
    subheading:
      "From the Gigaro beaches to the vineyards and Cap Lardier, a premium service in a preserved gulf.",
    paragraphs: [
      "La Croix-Valmer unrolls a preserved shoreline at the south of the Gulf of Saint-Tropez: the Gigaro beaches, the coves of Cap Lardier and the coastal path sit beside the hillside wine estates. In summer, beach access saturates quickly; a drop-off and pick-up close in avoid the hunt for a rare parking space.",
      "The commune has no airport: arrivals mostly come through Nice Côte d’Azur, around 1h20 to 1h30 away depending on summer traffic. We set the departure time to limit waiting, and offer Saint-Raphaël’s TGV station, some thirty minutes away, as an alternative transfer point.",
      "For villa and estate guests, one chauffeur links the Gigaro beaches, the vineyards and nearby Saint-Tropez, with discretion and flexible timing. Fixed fares agreed in advance or hourly hire: an immediate quote.",
    ],
    bullets: [
      "Gigaro beach shuttles",
      "Nice airport & Saint-Raphaël station transfers",
      "Vineyards & Cap Lardier",
    ],
  },
  process: {
    heading: "Book a chauffeur in La Croix-Valmer",
    steps: [
      "Tell us your route (village, Gigaro, airport, station) and times.",
      "We recommend the right vehicle and fare.",
      "The chauffeur tracks your flight or train and adjusts departure.",
      "Direct transfer or hourly hire across the gulf during your stay.",
    ],
  },
  faq: [
    cityPriceFaq("en", "La Croix-Valmer"),
    {
      q: "How long is the drive to Nice airport from La Croix-Valmer?",
      a: "Around 1h20 to 1h30 outside peak times, more on summer weekends and traffic peaks in the gulf. We adjust the departure and track your flight.",
    },
    {
      q: "Do you serve Saint-Raphaël TGV station?",
      a: "Yes. Saint-Raphaël station is around 30 to 40 minutes from La Croix-Valmer; it is a handy alternative transfer point for train arrivals and departures.",
    },
    {
      q: "Do you handle the link with Saint-Tropez?",
      a: "Yes, Saint-Tropez is very close; we run round trips to the port, restaurants and nights out, with safe late-night returns.",
    },
    {
      q: "How far ahead should I book?",
      a: "In high summer season, book as early as possible: demand is strong across the Gulf of Saint-Tropez. We also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele and villa stays.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes sedan, premium van (V-Class), SUV and minibus depending on group size and luggage, suited to the long run from Nice or Cannes.",
    },
    {
      q: "Do you serve the Gigaro beaches?",
      a: "Yes, we run round trips to the Gigaro beaches and the start of the Cap Lardier path, with no parking constraint and flexible timing in summer.",
    },
  ],
  relatedLinks: [
    {
      label: "Airport transfers in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    { label: "Airport transfers in Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Private chauffeur on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Car rental with delivery in Saint-Tropez",
      href: "/en/location/car-rental-saint-tropez",
    },
  ],
  cta: {
    title: "Your private chauffeur in La Croix-Valmer",
    text: "Nice airport transfer, Saint-Raphaël station or a shuttle to Gigaro: book your chauffeur, quick reply 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service La Croix-Valmer — private chauffeur & transfers",
    description:
      "Private car service in La Croix-Valmer: Nice airport transfer, Saint-Raphaël TGV station, Gigaro beach shuttles and the Saint-Tropez link.",
    canonical: "/en/chauffeur/la-croix-valmer",
  },
};

export default laCroixValmerEn;
